// Copyright 2024, Imprivata, Inc.  All rights reserved.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import { LocaleProvider } from './i18n';
import SetupRoutes from './setup/SetupRoutes';
import './styles/index.less';
import '@imprivata-cloud/design-system/style.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useInit from './hooks/useInit';
import PortalRoutes from './portal/PortalRoutes';
import SelfServiceRoutes from './self-service/SelfServiceRoutes';
import { EventProvider } from '@imprivata-cloud/core';

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);
const queryClient = new QueryClient();

export enum RootRoutes {
  OLD_SELF_SERVICE_LANDING_PAGE = '/self-service',
  SELF_SERVICE = '/me',
  SETUP = '/setup',
}

// App entrypoint
const App = () => {
  const location = useLocation();
  useInit();
  return (
    <QueryClientProvider client={queryClient}>
      <EventProvider>
        <Routes>
          <Route path={RootRoutes.SETUP.concat('/*')} element={<SetupRoutes />} />
          <Route path={RootRoutes.SELF_SERVICE.concat('/*')} element={<SelfServiceRoutes />} />
          {/* TODO: update to a redirect element */}
          <Route
            path={RootRoutes.OLD_SELF_SERVICE_LANDING_PAGE.concat('/*')}
            element={
              <Navigate
                to={{
                  pathname: location.pathname.replace(
                    RootRoutes.OLD_SELF_SERVICE_LANDING_PAGE,
                    RootRoutes.SELF_SERVICE,
                  ),
                  search: location.search,
                }}
                replace
              />
            }
          />
          <Route path="*" element={<PortalRoutes />} />
        </Routes>
      </EventProvider>
    </QueryClientProvider>
  );
};

// Render the application
root.render(
  <React.StrictMode>
    <LocaleProvider>
      <Router>
        <App />
      </Router>
    </LocaleProvider>
  </React.StrictMode>,
);
